import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, PhoneCall, Bike, Shield, Phone, LogOut, Package, UserCog, KeyRound, Sparkles, History, FolderOpen, UserCheck, ClipboardCheck, Bookmark, CalendarClock } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const Layout = ({ children, onLogout }) => {
  const location = useLocation();
  const userName = localStorage.getItem('userName') || 'User';
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchPending = async () => {
      try {
        const token = localStorage.getItem('token');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${BACKEND_URL}/api/change-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingApprovals(res.data.length);
      } catch {}
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/scheduled-calls', label: 'Scheduled Calls', icon: CalendarClock },
    { path: '/accounts', label: 'Accounts', icon: Users },
    { path: '/products', label: 'Products', icon: Package },
    { path: '/calls/new', label: 'Enter Field Call', icon: Bike },
    { path: '/calls/telephonic', label: 'Enter Telephonic Call', icon: Phone },
    { path: '/leads', label: 'Leads', icon: Bookmark },
    { path: '/action-log', label: 'Action Log', icon: Shield, adminOnly: true },
    { path: '/calls/history', label: 'Call History', icon: History },
    { path: '/documents', label: 'Documents', icon: FolderOpen },
  ];

  if (isAdmin) {
    navItems.push({ path: '/approvals', label: 'Approvals', icon: ClipboardCheck, badge: pendingApprovals });
    navItems.push({ path: '/assign-accounts', label: 'Assign Accounts', icon: UserCheck });
    navItems.push({ path: '/users', label: 'Users', icon: UserCog });
  }

  return (
    <div className="h-screen flex overflow-hidden" style={{ background: 'linear-gradient(160deg, #F0FAFA 0%, #E0F5F2 50%, #E6F7F5 100%)' }}>
      {/* Sidebar — fixed to window height; menu scrolls on its own */}
      <aside className="w-64 h-screen flex-shrink-0 bg-white/90 backdrop-blur-xl border-r border-teal-100 flex flex-col shadow-xl shadow-teal-500/10">
        {/* Logo */}
        <div className="p-6 border-b border-teal-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-teal-700 to-emerald-400 shadow-lg shadow-teal-500/30">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-700 to-emerald-500 bg-clip-text text-transparent" style={{ fontFamily: 'Manrope, sans-serif' }}>
              VoiceCRM
            </h1>
          </div>
          <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100">
            <p className="text-sm font-medium text-slate-700">Welcome back,</p>
            <p className="text-base font-semibold text-teal-700 truncate">{userName}</p>
            {isAdmin && (
              <span className="inline-block mt-2 text-xs px-2 py-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-full font-medium">
                Admin
              </span>
            )}
            <div className="mt-3 pt-3 border-t border-teal-100 flex flex-col gap-1">
              <Link
                to="/change-password"
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  location.pathname === '/change-password'
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-slate-500 hover:bg-white hover:text-teal-700'
                }`}
                data-testid="nav-change-password"
              >
                <KeyRound className="h-3.5 w-3.5" strokeWidth={2} />
                Change Password
              </Link>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-all w-full text-left"
                data-testid="logout-button"
              >
                <LogOut className="h-3.5 w-3.5" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 min-h-0 p-4 space-y-2 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-teal-700 to-emerald-500 text-white shadow-lg shadow-teal-500/30'
                    : 'text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon className="h-5 w-5" strokeWidth={2} />
                <span className="font-medium">{item.label}</span>
                {item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Wave decoration */}
        <div className="flex-shrink-0 overflow-hidden" style={{ height: '70px' }}>
          <svg viewBox="0 0 256 70" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
            <path d="M0,35 C40,10 80,60 128,35 C176,10 216,60 256,35 L256,70 L0,70 Z" fill="#0D7377" opacity="0.15"/>
            <path d="M0,48 C50,22 100,65 150,45 C200,22 230,58 256,45 L256,70 L0,70 Z" fill="#2EC4B6" opacity="0.2"/>
            <path d="M0,58 C60,42 110,68 160,55 C210,42 240,64 256,55 L256,70 L0,70 Z" fill="#10B981" opacity="0.28"/>
          </svg>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 h-screen overflow-auto">
        {children}
      </main>
    </div>
  );
};

export default Layout;
